import { Badge, Table } from "@mantine/core";
import styled from "styled-components";
import ScrollBox from "../common/Table/ScrollBox";
import { RotationPeriod } from "../../api/shiftRotationApi";
import { formatDayKey, todayKey } from "../../utils/shiftPattern";

/**
 * "BU HAFTA KİM NEREDE" tablosu (plan 2.34) — rotanın görünür hâli.
 *
 * Rota HESAPLANAN bir şey: hiçbir yerde "Ahmet 24 Ağustos'ta gece" diye bir
 * kayıt yok. Bu yüzden İK'nın onu GÖREBİLMESİ şart; kayıt olmadığında ekran
 * tek doğrulama yolu.
 *
 * ⚠ Matris burada HESAPLANMIYOR, backend'den hazır geliyor (21 Ağu kararı).
 * Bu bileşen yalnızca çiziyor — modulo/takvim-ayı matematiğini panelde ikinci
 * kez yazmak, biri düzeltilip diğeri unutulduğunda ekranı puantajdan sessizce
 * ayırırdı.
 *
 * Satır = ekip, kolon = dönem. Ters çevirmek (satır = dönem) de mümkündü ama
 * İK'nın sorusu "A ekibi bu ay ne yapıyor" biçiminde geliyor; ekibi satırda
 * tutmak o soruyu tek satır okumaya indiriyor.
 */
const RotationMatrix = ({
  periods,
  periodType,
}: {
  periods: RotationPeriod[];
  periodType?: "week" | "month";
}) => {
  if (!periods || periods.length === 0) {
    return <EmptyState>Gösterilecek dönem yok.</EmptyState>;
  }

  // Ekip sırası bütün dönemlerde aynı (backend rotanın `teamIds` sırasını
  // koruyor), o yüzden ilk dönemden okunuyor.
  const teams = periods[0].cells.map((cell) => ({
    teamId: cell.teamId,
    teamCode: cell.teamCode,
    teamName: cell.teamName,
  }));

  const today = todayKey();
  const periodLabel = periodType === "month" ? "Ay" : "Hafta";

  return (
    <TableWrapper>
      <Table style={{ borderCollapse: "separate" }} verticalSpacing="sm">
        <Table.Thead style={{ background: "#EEF2FF" }}>
          <Table.Tr>
            <StyledTh style={{ minWidth: 160 }}>Ekip</StyledTh>
            {periods.map((period) => {
              // İçinde bulunulan dönem vurgulanıyor: İK'nın ekranı açtığında
              // aradığı ilk şey "şu an hangi haftadayız".
              const isCurrent =
                period.startDate <= today && today <= period.endDate;
              return (
                <StyledTh key={period.index} style={{ minWidth: 150 }}>
                  <PeriodHead $current={isCurrent}>
                    <div>
                      {periodLabel} {period.index + 1}
                      {isCurrent && <NowTag>şu an</NowTag>}
                    </div>
                    <PeriodRange>
                      {formatDayKey(period.startDate)} –{" "}
                      {formatDayKey(period.endDate)}
                    </PeriodRange>
                  </PeriodHead>
                </StyledTh>
              );
            })}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {teams.map((team, rowIndex) => (
            <Table.Tr key={team.teamId}>
              <Table.Td>
                <TeamCell>
                  <Badge variant="light" color="indigo">
                    {team.teamCode}
                  </Badge>
                  <span>{team.teamName}</span>
                </TeamCell>
              </Table.Td>
              {periods.map((period) => {
                const cell = period.cells[rowIndex];
                const isCurrent =
                  period.startDate <= today && today <= period.endDate;
                return (
                  <Table.Td key={period.index}>
                    <CellBox $current={isCurrent}>
                      {cell?.shiftDefinitionId ? (
                        <Badge
                          variant="light"
                          color="indigo"
                          style={
                            cell.shiftColor
                              ? {
                                  background: `${cell.shiftColor}22`,
                                  color: cell.shiftColor,
                                }
                              : undefined
                          }
                        >
                          {cell.shiftCode} · {cell.shiftName}
                        </Badge>
                      ) : (
                        /*
                         * Boş hücrenin İKİ sebebi olabilir ve ikisi de "vardiya
                         * yok" demek: o dönem ekip DİNLENİYOR, ya da dönem
                         * rotanın geçerlilik aralığı dışında. İkisini ayırmak
                         * için hücreye rotanın tarihlerine bakmak gerekirdi;
                         * İK için ikisi de aynı sonuca çıkıyor.
                         */
                        <RestBadge>Dinlenme / rota dışı</RestBadge>
                      )}
                    </CellBox>
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </TableWrapper>
  );
};

export default RotationMatrix;

const TableWrapper = styled(ScrollBox)`
  /* Dönem sayısı arttıkça tablo genişliyor; sayfanın kendisi yatay kaymamalı. */
  overflow-x: auto;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 24px;
  color: #6b7280;
  font-size: 15px;
`;

const StyledTh = styled(Table.Td)`
  color: #4c4c4c;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`;

const PeriodHead = styled.div<{ $current: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${({ $current }) => ($current ? "#5c26ff" : "#4c4c4c")};
`;

const NowTag = styled.span`
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #5c26ff;
  background: #ede9fe;
  border-radius: 8px;
  padding: 1px 6px;
`;

const PeriodRange = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #9ca3af;
`;

const TeamCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #111827;
  font-size: 15px;
`;

const CellBox = styled.div<{ $current: boolean }>`
  display: flex;
  align-items: center;
  ${({ $current }) => $current && "font-weight: 500;"}
`;

const RestBadge = styled.span`
  color: #9ca3af;
  font-size: 13px;
`;
